// ResultsPanel - Affichage structuré des résultats d'analyse NGAP (sans données patient)

import { useState, useRef } from 'react';
import type { AnalyseResult } from '@/pages/analysis/engine';
import ReportDownload from './ReportDownload';

interface ResultsPanelProps {
  result: AnalyseResult;
  rapportNumber: string;
}

type ResultSection = 'cotations' | 'cumuls' | 'majorations' | 'limites' | 'total' | 'risque';

export default function ResultsPanel({ result, rapportNumber }: ResultsPanelProps) {
  const [expandedSections, setExpandedSections] = useState<Record<ResultSection, boolean>>({
    cotations: true,
    cumuls: true,
    majorations: true,
    limites: true,
    total: true,
    risque: true,
  });
  const [tab, setTab] = useState<'detail' | 'synthese'>('detail');
  const reportRef = useRef<HTMLDivElement>(null);

  const toggleSection = (s: ResultSection) => {
    setExpandedSections((prev) => ({ ...prev, [s]: !prev[s] }));
  };

  const risqueColorMap: Record<string, { bg: string; text: string; dot: string }> = {
    green: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    red: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    gray: { bg: 'bg-background-100', text: 'text-foreground-500', dot: 'bg-foreground-400' },
  };
  const rc = risqueColorMap[result.risque.couleur] || risqueColorMap.gray;

  // Compteurs contradictions
  const nbErrors = result.contradictions.filter((c) => c.type === 'error').length;
  const nbWarnings = result.contradictions.filter((c) => c.type === 'warning').length;
  const nbInfos = result.contradictions.filter((c) => c.type === 'info').length;

  const contradictionTypeConfig: Record<string, { bg: string; border: string; icon: string; iconBg: string; iconColor: string; badge: string; label: string }> = {
    error: {
      bg: 'bg-red-50', border: 'border-red-300/80', icon: 'ri-close-circle-fill', iconBg: 'bg-red-100', iconColor: 'text-red-600', badge: 'bg-red-100 text-red-700', label: 'Erreur',
    },
    warning: {
      bg: 'bg-amber-50', border: 'border-amber-300/80', icon: 'ri-error-warning-fill', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', label: 'Attention',
    },
    info: {
      bg: 'bg-blue-50', border: 'border-blue-200/70', icon: 'ri-information-fill', iconBg: 'bg-blue-100', iconColor: 'text-blue-500', badge: 'bg-blue-100 text-blue-600', label: 'Info',
    },
  };

  const severiteLabel: Record<string, string> = {
    critique: 'Critique',
    eleve: 'Élevée',
    modere: 'Modérée',
    faible: 'Faible',
  };

  return (
    <div ref={reportRef} className="space-y-4" data-report-content>
      {/* ═══ BANDEAU CONTRADICTIONS ═══ */}
      {result.contradictions.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-alert-fill text-lg text-red-500"></i>
            <h3 className="text-sm font-bold text-foreground-800 uppercase tracking-wider">
              Contradictions détectées
            </h3>
            <div className="flex items-center gap-1.5 ml-2">
              {nbErrors > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  {nbErrors} erreur{nbErrors > 1 ? 's' : ''}
                </span>
              )}
              {nbWarnings > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  {nbWarnings} attention{nbWarnings > 1 ? 's' : ''}
                </span>
              )}
              {nbInfos > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600">
                  {nbInfos} info{nbInfos > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          {result.contradictions.map((ctr) => {
            const cfg = contradictionTypeConfig[ctr.type];
            return (
              <div
                key={ctr.id}
                className={`${cfg.bg} border ${cfg.border} rounded-lg p-4`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full ${cfg.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <i className={`${cfg.icon} ${cfg.iconColor} text-base`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${cfg.badge} whitespace-nowrap`}>
                        {cfg.label}
                      </span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        ctr.severite === 'critique' ? 'bg-red-100 text-red-700' :
                        ctr.severite === 'eleve' ? 'bg-amber-100 text-amber-700' :
                        ctr.severite === 'modere' ? 'bg-amber-50 text-amber-600' :
                        'bg-blue-50 text-blue-600'
                      }`}>
                        Sévérité {severiteLabel[ctr.severite].toLowerCase()}
                      </span>
                      <h4 className="text-sm font-bold text-foreground-900">{ctr.titre}</h4>
                    </div>
                    <p className="text-xs text-foreground-700 leading-relaxed mt-1.5">
                      {ctr.description}
                    </p>
                    <div className="mt-2.5 flex items-start gap-2 bg-background-50/70 rounded-md p-2.5 border border-background-200/50">
                      <i className="ri-lightbulb-line text-accent-500 text-sm mt-0.5 flex-shrink-0"></i>
                      <p className="text-xs text-foreground-700 leading-relaxed">
                        <span className="font-semibold text-foreground-800">Suggestion : </span>
                        {ctr.suggestion}
                      </p>
                    </div>
                    {ctr.articles.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        <i className="ri-bookmark-line text-foreground-400 text-xs"></i>
                        {ctr.articles.map((art, i) => (
                          <span key={i} className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">
                            {art}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Résumé rapide + Score */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
              Résumé de l&apos;analyse
            </h4>
            <p className="text-sm text-foreground-800">{result.resumeAnalyse}</p>
            <p className="text-xs text-foreground-400 mt-1">
              {result.cotations.length} acte{result.cotations.length > 1 ? 's' : ''} identifié{result.cotations.length > 1 ? 's' : ''} — Rapport {rapportNumber}
            </p>
          </div>
          <div className={`flex items-center gap-3 ${rc.bg} rounded-lg px-4 py-3 flex-shrink-0`}>
            <div className={`w-3 h-3 rounded-full ${rc.dot}`}></div>
            <div>
              <span className={`text-xs font-semibold ${rc.text} block`}>{result.risque.niveau}</span>
              <span className={`text-2xl font-bold ${rc.text}`}>{result.risque.score}</span>
              <span className="text-xs text-foreground-400">/100</span>
            </div>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-1 mt-4 bg-background-100 rounded-full p-1 w-fit">
          <button
            onClick={() => setTab('detail')}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              tab === 'detail' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Analyse détaillée
          </button>
          <button
            onClick={() => setTab('synthese')}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              tab === 'synthese' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Synthèse
          </button>
        </div>
      </div>

      {tab === 'detail' ? (
        <>
          {/* 1. Cotations */}
          <SectionCard
            title="Actes identifiés et cotés"
            icon="ri-file-list-3-line"
            expanded={expandedSections.cotations}
            onToggle={() => toggleSection('cotations')}
            badge={`${result.cotations.length} acte${result.cotations.length > 1 ? 's' : ''}`}
          >
            {result.cotations.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-2 pr-2 font-semibold text-foreground-500 whitespace-nowrap">Code</th>
                      <th className="text-left py-2 pr-2 font-semibold text-foreground-500">Acte</th>
                      <th className="text-center py-2 pr-2 font-semibold text-foreground-500 whitespace-nowrap">Coef.</th>
                      <th className="text-center py-2 pr-2 font-semibold text-foreground-500 whitespace-nowrap">Passage</th>
                      <th className="text-center py-2 pr-2 font-semibold text-foreground-500 whitespace-nowrap">Taux</th>
                      <th className="text-right py-2 font-semibold text-foreground-500 whitespace-nowrap">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.cotations.map((c) => (
                      <tr key={c.id} className="border-b border-background-100 last:border-0">
                        <td className="py-2.5 pr-2">
                          <span className="inline-block px-1.5 py-0.5 bg-primary-100 text-primary-700 font-bold rounded whitespace-nowrap">
                            {c.code}
                          </span>
                        </td>
                        <td className="py-2.5 pr-2 text-foreground-800">{c.label}</td>
                        <td className="py-2.5 pr-2 text-center text-foreground-500">{c.coefficient}</td>
                        <td className="py-2.5 pr-2 text-center text-foreground-500 whitespace-nowrap">{c.passage}</td>
                        <td className="py-2.5 pr-2 text-center">
                          {c.tauxFacturation === 100 ? (
                            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-green-600">
                              <i className="ri-check-line text-xs"></i>100%
                            </span>
                          ) : c.tauxFacturation === 50 ? (
                            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-amber-600">
                              50%
                            </span>
                          ) : c.tauxFacturation === 0 ? (
                            <span className="inline-flex items-center gap-0.5 text-xs text-foreground-300">
                              Gratuit
                            </span>
                          ) : (
                            <span className="text-xs text-foreground-400">—</span>
                          )}
                        </td>
                        <td className="py-2.5 text-right font-semibold text-foreground-950 whitespace-nowrap">{c.montant.toFixed(2).replace('.', ',')} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-foreground-400 py-2">Aucun acte NGAP identifiable.</p>
            )}
          </SectionCard>

          {/* 2. Cumuls */}
          <SectionCard
            title="Vérification des cumuls"
            icon="ri-git-merge-line"
            expanded={expandedSections.cumuls}
            onToggle={() => toggleSection('cumuls')}
            badge={`${result.cumuls.filter((c) => c.compatible).length}/${result.cumuls.length} OK`}
          >
            <div className="space-y-3">
              {result.cumuls.map((cumul, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-background-100 rounded-md">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {cumul.compatible ? (
                      <i className="ri-checkbox-circle-fill text-green-500 text-lg"></i>
                    ) : (
                      <i className="ri-close-circle-fill text-red-500 text-lg"></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-semibold text-foreground-800">
                        {cumul.acte1} {cumul.acte2 !== '—' && `+ ${cumul.acte2}`}
                      </span>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${cumul.compatible ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {cumul.compatible ? 'Cumul OK' : 'Incompatible'}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{cumul.regle}</p>
                    {cumul.article !== '—' && (
                      <p className="text-xs text-foreground-400 mt-1 italic">{cumul.article}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* 3. Majorations */}
          <SectionCard
            title="Majorations applicables"
            icon="ri-add-circle-line"
            expanded={expandedSections.majorations}
            onToggle={() => toggleSection('majorations')}
            badge={`${result.majorations.filter((m) => m.applicable).length} appliquée${result.majorations.filter((m) => m.applicable).length > 1 ? 's' : ''}`}
          >
            <div className="space-y-3">
              {result.majorations.map((maj, i) => (
                <div key={i} className={`p-3 rounded-md border ${maj.applicable ? 'bg-accent-50 border-accent-200/40' : 'bg-background-100 border-background-200/50'}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-foreground-800">{maj.code}</span>
                      <span className="text-xs text-foreground-600">{maj.nom}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {maj.applicable ? (
                        <>
                          <span className="text-xs font-semibold text-accent-700">{maj.montant}</span>
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">Appliquée</span>
                        </>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-background-200 text-foreground-500 whitespace-nowrap">Non applicable</span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">{maj.raison}</p>
                  <p className="text-xs text-foreground-400 mt-1 italic">{maj.article}</p>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* 4. Limites */}
          <SectionCard
            title="Respect des limites quotidiennes"
            icon="ri-speed-line"
            expanded={expandedSections.limites}
            onToggle={() => toggleSection('limites')}
            badge={`${result.limites.filter((l) => l.ok).length}/${result.limites.length} OK`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="text-left py-2 pr-2 font-semibold text-foreground-500">Code</th>
                    <th className="text-left py-2 pr-2 font-semibold text-foreground-500">Acte</th>
                    <th className="text-left py-2 pr-2 font-semibold text-foreground-500">Limite</th>
                    <th className="text-center py-2 pr-2 font-semibold text-foreground-500">Côté</th>
                    <th className="text-center py-2 font-semibold text-foreground-500">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {result.limites.map((lim, i) => (
                    <tr key={i} className="border-b border-background-100 last:border-0">
                      <td className="py-2.5 pr-2 font-semibold text-foreground-800">{lim.code}</td>
                      <td className="py-2.5 pr-2 text-foreground-600">{lim.nom}</td>
                      <td className="py-2.5 pr-2 text-foreground-500">{lim.limite}</td>
                      <td className="py-2.5 pr-2 text-center text-foreground-800">{lim.actuel}</td>
                      <td className="py-2.5 text-center">
                        {lim.ok ? (
                          <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                            <i className="ri-check-line text-xs"></i> OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                            <i className="ri-close-line text-xs"></i> Dépassé
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* 5. Total */}
          <SectionCard
            title="Récapitulatif de facturation"
            icon="ri-money-euro-circle-line"
            expanded={expandedSections.total}
            onToggle={() => toggleSection('total')}
            accent
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between py-2 border-b border-background-100">
                <span className="text-sm text-foreground-600">Sous-total actes ({result.cotations.length} cotation{result.cotations.length > 1 ? 's' : ''})</span>
                <span className="text-sm font-semibold text-foreground-800">{result.total.sousTotalActes}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-background-100">
                <span className="text-sm text-foreground-600">Majorations</span>
                <span className="text-sm font-semibold text-accent-600">{result.total.totalMajorations}</span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-base font-bold text-foreground-950">Total</span>
                <span className="text-xl font-bold text-primary-600">{result.total.totalGeneral}</span>
              </div>
            </div>
            <p className="text-xs text-foreground-400 mt-3 pt-3 border-t border-background-100">
              Ce total inclut les actes, majorations et l'IFD (2,75 € par période visitée). Les indemnités kilométriques (IK) ne sont pas calculées — elles dépendent de la distance réelle.
            </p>
          </SectionCard>

          {/* 6. Score de risque */}
          <SectionCard
            title="Score de sécurité CPAM"
            icon="ri-shield-flash-line"
            expanded={expandedSections.risque}
            onToggle={() => toggleSection('risque')}
          >
            <div className={`p-4 rounded-lg ${rc.bg} border border-background-200/50`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${rc.bg}`}>
                  <span className={`text-xl font-bold ${rc.text}`}>{result.risque.score}</span>
                </div>
                <div>
                  <span className={`text-sm font-semibold ${rc.text} block`}>Risque {result.risque.niveau.toLowerCase()}</span>
                  <span className="text-xs text-foreground-400">Score de conformité NGAP</span>
                </div>
              </div>
              <ul className="space-y-1.5">
                {result.risque.details.map((d, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className={`text-xs mt-0.5 flex-shrink-0 ${d.startsWith('ATTENTION') ? 'ri-error-warning-line text-red-500' : 'ri-check-line text-green-500'}`}></i>
                    <span className="text-xs text-foreground-600">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </SectionCard>
        </>
      ) : (
        /* Synthèse */
        <div className="space-y-4">
          {/* Rappel contradictions en synthèse */}
          {result.contradictions.length > 0 && (
            <div className="bg-red-50 border border-red-200/70 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <i className="ri-alert-fill text-sm"></i>
                {result.contradictions.length} contradiction{result.contradictions.length > 1 ? 's' : ''} à corriger
              </h4>
              <ul className="space-y-2">
                {result.contradictions.map((ctr) => {
                  const cfg = contradictionTypeConfig[ctr.type];
                  return (
                    <li key={ctr.id} className="flex items-start gap-2">
                      <i className={`${cfg.icon} ${cfg.iconColor} text-xs mt-0.5 flex-shrink-0`}></i>
                      <div>
                        <span className="text-xs font-semibold text-foreground-800">{ctr.titre}</span>
                        <p className="text-xs text-foreground-600 mt-0.5">{ctr.suggestion}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
              <i className="ri-lightbulb-line mr-1"></i>
              Points d&apos;attention
            </h4>
            <ul className="space-y-2">
              {result.avertissements.map((a, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-arrow-right-line text-accent-500 text-xs mt-0.5 flex-shrink-0"></i>
                  <span className="text-xs text-foreground-700 leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
              <i className="ri-bookmark-line mr-1"></i>
              Sources réglementaires consultées
            </h4>
            <ul className="space-y-1.5">
              {result.sources.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-bookmark-line text-accent-500 text-xs mt-0.5 flex-shrink-0"></i>
                  <span className="text-xs text-foreground-700">{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              Justification du score de sécurité ({result.risque.score}/100)
            </h4>
            <ul className="space-y-1.5">
              {result.risque.details.map((d, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className={`text-xs mt-0.5 flex-shrink-0 ${d.startsWith('ATTENTION') ? 'ri-error-warning-line text-red-500' : 'ri-check-line text-green-500'}`}></i>
                  <span className="text-xs text-foreground-600">{d}</span>
                </li>
              ))}
            </ul>
          </div>

          <ReportDownload rapportNumber={rapportNumber} result={result} />
        </div>
      )}

      {/* Bottom actions */}
      {tab === 'detail' && (
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <ReportDownload rapportNumber={rapportNumber} result={result} />
        </div>
      )}
    </div>
  );
}

function SectionCard({
  title,
  icon,
  children,
  expanded,
  onToggle,
  badge,
  accent,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  badge?: string;
  accent?: boolean;
}) {
  return (
    <div className={`bg-background-50 border rounded-lg overflow-hidden transition-all ${accent ? 'border-primary-300/60' : 'border-background-200/70'}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-background-100/50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2.5">
          <i className={`${icon} text-primary-500 text-sm`}></i>
          <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">{title}</span>
          {badge && (
            <span className="text-xs text-foreground-400 ml-1">({badge})</span>
          )}
        </div>
        {expanded ? (
          <i className="ri-arrow-up-s-line text-foreground-400 text-sm"></i>
        ) : (
          <i className="ri-arrow-down-s-line text-foreground-400 text-sm"></i>
        )}
      </button>
      {expanded && (
        <div className="px-5 pb-5">
          {children}
        </div>
      )}
    </div>
  );
}